import React, { useCallback, useEffect, useState } from 'react';
import {
  Building2,
  CloudOff,
  IdCard,
  Inbox,
  Pencil,
  RefreshCw,
  Trash2,
  User,
} from 'lucide-react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  EmptyState,
  Pill,
  SegmentedControl,
} from '../../../components/ui';
import { listRecords } from '../cache';
import { fileSize, plural, timeAgo, timeUntil } from '../format';
import { discardItem, retryItem } from '../outbox';
import { activeVersion } from '../serviceWorkerRegistration';
import { refreshOfflineState, useOfflineState } from '../store';
import { syncNow } from '../sync';
import type { CachedRecord, OutboxItem } from '../types';
import { CaptureSheet } from './CaptureSheet';

const KIND_ICON = {
  'stakeholder.create': User,
  'stakeholder.update': Pencil,
  'organization.create': Building2,
  'organization.update': Pencil,
  'businessCard.upload': IdCard,
} as const;

const KIND_LABEL = {
  'stakeholder.create': 'New person',
  'stakeholder.update': 'Edit person',
  'organization.create': 'New organisation',
  'organization.update': 'Edit organisation',
  'businessCard.upload': 'Business card',
} as const;

/** Never claim a queued write is done; say exactly where it stands. */
const statusPill = (item: OutboxItem): { tone: 'amber' | 'blue' | 'red'; text: string } => {
  if (item.status === 'syncing') return { tone: 'blue', text: 'Sending' };
  if (item.status === 'blocked') return { tone: 'red', text: 'Needs you' };
  return { tone: 'amber', text: 'Waiting' };
};

const QueueRow: React.FC<{
  item: OutboxItem;
  now: number;
  onFix: (item: OutboxItem) => void;
  onDiscarded: (removed: number) => void;
}> = ({ item, now, onFix, onDiscarded }) => {
  const Icon = KIND_ICON[item.kind] ?? Inbox;
  const pill = statusPill(item);
  const [busy, setBusy] = useState<'retry' | 'discard' | null>(null);

  const meta = [
    `Queued ${timeAgo(item.createdAt, now)}`,
    item.attempts > 0 ? `tried ${item.attempts}×` : null,
    item.attempts > 0 && item.lastAttemptAt ? `last ${timeAgo(item.lastAttemptAt, now)}` : null,
    item.status === 'pending' && item.nextAttemptAt && item.nextAttemptAt > now
      ? `next ${timeUntil(item.nextAttemptAt, now)}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <li className="px-4 py-3">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-500">
          <Icon className="h-4 w-4" strokeWidth={1.75} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className="truncate text-[13px] text-ink">{item.label}</p>
            <Pill tone={pill.tone}>{pill.text}</Pill>
          </div>
          <p className="truncate text-[12px] text-neutral-500">
            {KIND_LABEL[item.kind] ?? 'Change'}
            {item.detail ? ` · ${item.detail}` : ''}
          </p>
          <p className="mt-0.5 text-[12px] tabular-nums text-neutral-400">{meta}</p>

          {item.error && (
            <p
              className={`mt-2 rounded-xl px-3 py-2 text-[12px] ${
                item.status === 'blocked'
                  ? 'bg-primary-50 text-primary-700'
                  : 'bg-neutral-50 text-neutral-500'
              }`}
            >
              {item.error.message}
            </p>
          )}

          <div className="mt-2 flex items-center gap-2">
            {item.status === 'blocked' && item.body && item.kind !== 'businessCard.upload' && (
              <Button size="xs" variant="secondary" onClick={() => onFix(item)}>
                <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
                Fix
              </Button>
            )}
            <Button
              size="xs"
              variant="secondary"
              disabled={busy !== null}
              onClick={async () => {
                setBusy('retry');
                await retryItem(item.id);
                await syncNow();
                setBusy(null);
              }}
            >
              <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.75} />
              Retry
            </Button>
            <Button
              size="xs"
              variant="ghost"
              disabled={busy !== null}
              onClick={async () => {
                setBusy('discard');
                const removed = await discardItem(item.id);
                await refreshOfflineState();
                // a card photo can belong to this person; say so rather than
                // letting it disappear quietly
                onDiscarded(removed);
                setBusy(null);
              }}
            >
              <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
              Discard
            </Button>
          </div>
        </div>
      </div>
    </li>
  );
};

const SavedRow: React.FC<{ record: CachedRecord; now: number }> = ({ record, now }) => {
  const name =
    (record.data as any)?.name ??
    [(record.data as any)?.firstName, (record.data as any)?.lastName].filter(Boolean).join(' ') ??
    record.id;
  const href = `${record.kind === 'stakeholder' ? '/stakeholders' : '/organizations'}/${record.id}`;

  return (
    <li>
      <a
        href={href}
        className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-neutral-50"
      >
        <span className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-500">
          {record.kind === 'stakeholder' ? (
            <User className="h-4 w-4" strokeWidth={1.75} />
          ) : (
            <Building2 className="h-4 w-4" strokeWidth={1.75} />
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] text-ink">{name || 'Untitled'}</span>
          <span className="block text-[12px] text-neutral-400">
            Stored copy · saved {timeAgo(record.cachedAt, now)}
          </span>
        </span>
      </a>
    </li>
  );
};

/**
 * Everything the officer needs to trust the queue: what is waiting, what went
 * wrong, and what is readable without a connection.
 */
export const OutboxSheet: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const { items, online, syncing, lastSyncAt, cache, unavailable } = useOfflineState();
  const [tab, setTab] = useState<'waiting' | 'saved'>('waiting');
  const [saved, setSaved] = useState<CachedRecord[]>([]);
  const [now, setNow] = useState(() => Date.now());
  const [build, setBuild] = useState<string | null>(null);
  const [capture, setCapture] = useState<'stakeholder' | 'organization' | null>(null);
  const [fixing, setFixing] = useState<OutboxItem | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const loadSaved = useCallback(async () => {
    try {
      setSaved(await listRecords());
    } catch {
      setSaved([]);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    setNotice(null);
    void refreshOfflineState();
    void loadSaved();
    void activeVersion().then(setBuild);
    const timer = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(timer);
  }, [open, loadSaved]);

  const waiting = items.filter((item) => item.status !== 'blocked');
  const blocked = items.filter((item) => item.status === 'blocked');
  const ordered = [...blocked, ...waiting];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        label="Sync queue"
        handle
        className="h-[88dvh] sm:h-auto sm:max-h-[80vh] sm:max-w-lg"
      >
        <DialogHeader
          title="Sync queue"
          eyebrow={online ? 'Connected' : 'No connection'}
          onClose={onClose}
        />
        <DialogBody className="pt-2">
          <div className="mb-3 flex items-center justify-between gap-3 rounded-2xl border border-neutral-200 px-4 py-3">
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 text-[13px] text-ink">
                {online ? 'Online' : (
                  <>
                    <CloudOff className="h-4 w-4 text-neutral-400" strokeWidth={1.75} />
                    Offline
                  </>
                )}
              </p>
              <p className="text-[12px] text-neutral-400">
                {items.length === 0
                  ? `Everything sent · last sync ${timeAgo(lastSyncAt, now)}`
                  : `${plural(items.length, 'item', 'items')} waiting · last sync ${timeAgo(lastSyncAt, now)}`}
              </p>
            </div>
            <Button
              size="sm"
              variant="secondary"
              // never gated on the offline flag: trying is how you find out
              // the connection came back
              disabled={syncing || items.length === 0}
              onClick={() => void syncNow()}
            >
              <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} strokeWidth={1.75} />
              {syncing ? 'Syncing' : 'Sync now'}
            </Button>
          </div>

          <SegmentedControl
            label="Queue view"
            className="mb-3"
            value={tab}
            onChange={setTab}
            items={[
              { value: 'waiting', label: `Waiting${items.length ? ` (${items.length})` : ''}` },
              { value: 'saved', label: `Saved${saved.length ? ` (${saved.length})` : ''}` },
            ]}
          />

          {notice && (
            <p className="mb-3 rounded-xl bg-neutral-50 px-3.5 py-2.5 text-[12px] text-neutral-500">
              {notice}
            </p>
          )}

          {unavailable && (
            <p className="mb-3 rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
              This browser will not let Staketory store anything offline. Captures need a connection.
            </p>
          )}

          {tab === 'waiting' ? (
            ordered.length === 0 ? (
              <EmptyState icon={Inbox} title="Nothing waiting" />
            ) : (
              <ul className="divide-y divide-neutral-200/70 rounded-2xl border border-neutral-200">
                {ordered.map((item) => (
                  <QueueRow
                    key={item.id}
                    item={item}
                    now={now}
                    onFix={(target) => {
                      setFixing(target);
                      setCapture(target.kind.startsWith('organization') ? 'organization' : 'stakeholder');
                    }}
                    onDiscarded={(removed) =>
                      setNotice(
                        removed > 1
                          ? `Discarded ${removed} queued items that belonged together.`
                          : 'Discarded.',
                      )
                    }
                  />
                ))}
              </ul>
            )
          ) : saved.length === 0 ? (
            <EmptyState icon={CloudOff} title="Nothing saved yet" />
          ) : (
            <>
              <ul className="divide-y divide-neutral-200/70 rounded-2xl border border-neutral-200">
                {saved.slice(0, 60).map((record) => (
                  <SavedRow key={record.key} record={record} now={now} />
                ))}
              </ul>
              <p className="mt-2 px-1 text-[12px] text-neutral-400">
                {plural(cache?.records ?? saved.length, 'record', 'records')} · {fileSize(cache?.bytes)} on this
                phone
              </p>
            </>
          )}
          <p className="mt-4 text-center text-[12px] text-neutral-400">
            {build ? `Offline build ${build}` : 'Offline support not installed'}
          </p>
        </DialogBody>
        <DialogFooter className="justify-between">
          <Button size="sm" variant="ghost" onClick={() => setCapture('organization')}>
            <Building2 className="h-4 w-4" strokeWidth={1.75} />
            Organisation
          </Button>
          <Button size="sm" variant="primary" onClick={() => setCapture('stakeholder')}>
            <User className="h-4 w-4" strokeWidth={2} />
            Capture person
          </Button>
        </DialogFooter>
      </Dialog>

      <CaptureSheet
        kind={capture}
        item={fixing}
        onClose={() => {
          setCapture(null);
          setFixing(null);
        }}
      />
    </>
  );
};
